import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, Loader2, PartyPopper } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/i18n";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

export default function SurveyLanding() {
  const { machineId: paramMachine } = useParams();
  const machineId = paramMachine || "machine-01";
  const productsPath = paramMachine ? `/vending/${paramMachine}/products` : "/products";
  const navigate = useNavigate();
  const { t, tl, lang } = useLang();

  const [survey, setSurvey] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState("");

  const goToProducts = () => navigate(productsPath);

  useEffect(() => {
    let active = true;
    api.post("/public/events", { type: "visit", machine_id: machineId }).catch(() => {});
    (async () => {
      try {
        const { data } = await api.get("/public/survey");
        if (!active) return;
        // cooldown check
        const stored = localStorage.getItem(`caneat_survey_${machineId}`);
        if (stored && data.cooldown_hours) {
          const last = parseInt(stored, 10);
          const elapsedH = (Date.now() - last) / 3600000;
          if (elapsedH < data.cooldown_hours) { goToProducts(); return; }
        }
        if (!data.active) { goToProducts(); return; }
        setSurvey(data);
        api.post("/public/events", { type: "survey_start", machine_id: machineId }).catch(() => {});
      } catch {
        goToProducts();
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [machineId]);

  const submit = async () => {
    if (!selected) { setError(t("please_select")); return; }
    setSubmitting(true);
    setError("");
    try {
      await api.post("/public/survey/respond", { option_id: selected, machine_id: machineId, lang });
      localStorage.setItem(`caneat_survey_${machineId}`, String(Date.now()));
      setDone(true);
      setTimeout(goToProducts, 1600);
    } catch {
      setSubmitting(false);
      setError(t("please_select"));
    }
  };

  return (
    <div className="caneat-doodle-bg caneat-wave relative min-h-[100dvh] overflow-hidden">
      <div className="relative z-10 mx-auto flex min-h-[100dvh] max-w-md flex-col px-5 pb-10 pt-5">
        <div className="flex items-center justify-between">
          <span className="text-xs font-bold tracking-[0.2em] text-white/70">{machineId.toUpperCase()}</span>
          <LanguageSwitcher variant="light" />
        </div>

        <AnimatePresence mode="wait">
          {done ? (
            <motion.div
              key="done"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex flex-1 flex-col items-center justify-center text-center"
              data-testid="survey-success"
            >
              <motion.div
                initial={{ scale: 0, rotate: -20 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 260, damping: 14 }}
                className="mb-6 flex h-28 w-28 items-center justify-center rounded-full bg-white shadow-2xl"
              >
                <PartyPopper className="h-14 w-14 text-caneat-orange" strokeWidth={2.2} />
              </motion.div>
              <h2 className="caneat-title text-4xl text-white text-shadow-ink">{t("thanks_title")}</h2>
              <p className="mt-2 text-base font-semibold text-white/90">{t("thanks_sub")}</p>
            </motion.div>
          ) : (
            <motion.div
              key="survey"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              className="flex flex-1 flex-col"
            >
              <div className="mb-6 mt-6 flex flex-col items-center text-center">
                <motion.img
                  src="/brand/caneat-logo-white.png"
                  alt="CAN'EAT"
                  className="h-32 w-auto animate-float drop-shadow-xl"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                />
                <p className="mt-6 max-w-xs text-lg font-bold leading-snug text-white text-shadow-ink">
                  {t("survey_teaser")}
                </p>
              </div>

              {loading ? (
                <div className="flex flex-1 items-center justify-center">
                  <Loader2 className="h-8 w-8 animate-spin text-white" />
                </div>
              ) : survey ? (
                <>
                  <div className="rounded-3xl bg-white p-5 shadow-2xl">
                    <h1 className="caneat-title mb-4 text-2xl leading-tight text-caneat-ink" data-testid="survey-question">
                      {tl(survey.question)}
                    </h1>
                    <div className="flex flex-col gap-3">
                      {survey.options.map((opt) => {
                        const active = selected === opt.id;
                        return (
                          <motion.button
                            key={opt.id}
                            data-testid={`survey-option-${opt.id}`}
                            whileTap={{ scale: 0.97 }}
                            onClick={() => { setSelected(opt.id); setError(""); }}
                            className={`flex min-h-[58px] items-center justify-between rounded-2xl border-2 px-5 text-left text-base font-bold transition-all ${
                              active
                                ? "border-caneat-orange bg-caneat-orange text-white shadow-lg"
                                : "border-caneat-cream bg-caneat-pageCream text-caneat-ink hover:border-caneat-orange/40"
                            }`}
                          >
                            <span>{tl(opt.label)}</span>
                            {active && <Check className="h-5 w-5 shrink-0" strokeWidth={3} />}
                          </motion.button>
                        );
                      })}
                    </div>
                  </div>

                  {error && (
                    <p className="mt-3 text-center text-sm font-bold text-white" data-testid="survey-error">
                      {error}
                    </p>
                  )}

                  <motion.button
                    whileTap={{ scale: 0.97 }}
                    onClick={submit}
                    disabled={submitting}
                    data-testid="survey-submit"
                    className="mt-6 flex min-h-[60px] w-full items-center justify-center rounded-full bg-caneat-ink text-lg font-extrabold uppercase tracking-wider text-white shadow-[0_8px_24px_rgba(0,0,0,0.25)] disabled:opacity-60"
                  >
                    {submitting ? <Loader2 className="h-6 w-6 animate-spin" /> : t("validate")}
                  </motion.button>
                </>
              ) : null}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
